import React, { PropsWithChildren, useEffect, useState } from 'react';

import { ScrollArea, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@column/ui';

import { HelpSidebarType, useHelpSidebarStore } from '~/stores/HelpSidebar';

export interface HelpSheetProps {
  helpSidebars: HelpSidebarType[];
  headline?: string;
}

export const HelpSheet: React.FC<PropsWithChildren<HelpSheetProps>> = ({ helpSidebars, headline, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { closeHelpSidebar, getHelpSidebarIsOpen, getHelpSidebar } = useHelpSidebarStore();

  // Subscribe to store changes
  useEffect(
    () =>
      useHelpSidebarStore.subscribe(
        (state) => ({
          helpSidebar: state.getHelpSidebar(),
          isOpen: state.getHelpSidebarIsOpen(),
        }),
        ({ helpSidebar, isOpen: storeIsOpen }) => {
          setIsOpen(storeIsOpen && helpSidebar !== undefined && helpSidebars.includes(helpSidebar));
        }
      ),
    [helpSidebars]
  );

  // Initialize state
  useEffect(() => {
    const helpSidebar = getHelpSidebar();
    setIsOpen(getHelpSidebarIsOpen() && helpSidebar !== undefined && helpSidebars.includes(helpSidebar));
  }, [helpSidebars, getHelpSidebar, getHelpSidebarIsOpen]);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeHelpSidebar()}>
      <SheetContent
        side="right"
        className="w-[400px] sm:max-w-[400px] p-0 flex flex-col gap-0 !border-l-0 text-sm shadow-[-8px_0_24px_rgba(0,0,0,0.12)]"
      >
        {headline && (
          <SheetHeader className="px-5 py-4 border-b border-gray-200 dark:border-white/10">
            <SheetTitle className="text-sm font-medium">{headline}</SheetTitle>
            <SheetDescription className="sr-only">{headline} panel</SheetDescription>
          </SheetHeader>
        )}
        <ScrollArea fadeEdges className="flex-1 min-h-0 !rounded-none">
          <div className="p-5">{children}</div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
